import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UAParser } from 'ua-parser-js';
import { environment } from 'src/environments/environment';
import { SupabaseService } from './base/supabase.service';
import { EventTypes } from '../models/events.model';
import { buildDescriptor } from '../build-descriptor';

/**
 * Watches the KYC guided-capture flow for a **frozen main thread** and logs it to
 * the DB — the failure mode where the tab shows "infinite loading" after the
 * back-of-document upload because a synchronous, main-thread step (e.g. the
 * on-device onnx classify/segmentation, or a large image decode) blocks every
 * timer and event, so the page itself can no longer report anything.
 *
 * Detection can't live on the main thread (it's the thing that freezes), so it
 * runs in {@link ./capture-watchdog.worker}. The main thread:
 *   - `setPhase(name)` — a breadcrumb before each heavy step, so a stall is
 *     attributed to the exact phase, and
 *   - pings every second.
 * The worker keeps its own clock; if pings stop for longer than the deadline it
 * POSTs a `capture_stall` event straight to /event_push (the main thread can't).
 *
 * The event reuses {@link EventTypes.KYC_PRECHECK_NOT_CONFIRMED} with
 * `outcome: 'capture_stall'` (same no-migration pattern the SomosKycV1 provider
 * already uses for `rg_cpf_acknowledged`), so it needs no new event type.
 */
@Injectable({ providedIn: 'root' })
export class CaptureWatchdogService {
  private worker: Worker | null = null;
  private workerBroken = false;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /** Provider/case context merged into every stall event (claim, method, device caps…). */
  private base: Record<string, unknown> = {};

  /** A phase legitimately taking longer than this is treated as a stall. */
  private static readonly DEADLINE_MS = 10_000;
  private static readonly EVENT_TYPE = EventTypes.KYC_PRECHECK_NOT_CONFIRMED;

  constructor(private readonly sbs: SupabaseService, private readonly router: Router) {}

  /** Begin watching. `base` is context merged into every stall event. Idempotent. */
  async start(base: Record<string, unknown> = {}): Promise<void> {
    this.base = base ?? {};
    const w = this.ensureWorker();
    if (!w) return;
    this.running = true;
    w.postMessage({ type: 'config', deadlineMs: CaptureWatchdogService.DEADLINE_MS });
    await this.setPhase('capture_active', {});
    this.heartbeat ??= setInterval(() => this.worker?.postMessage({ type: 'ping' }), 1000);
  }

  /** Merge more fields into the base context carried by future stall events. */
  extendContext(ctx: Record<string, unknown>): void {
    this.base = { ...this.base, ...ctx };
  }

  /**
   * Breadcrumb: record the phase we are ABOUT to run. Call this immediately before
   * a potentially-blocking step so that, if the thread freezes inside it, the
   * watchdog attributes the stall to `phase`. No-op until {@link start}.
   */
  async setPhase(phase: string, ctx: Record<string, unknown> = {}): Promise<void> {
    const w = this.ensureWorker();
    if (!w || !this.running) return;
    try {
      const request = await this.buildRequest(phase, ctx);
      w.postMessage({ type: 'phase', request });
    } catch {
      // Token/router lookup failed — leave the previous breadcrumb in place.
    }
  }

  /** Stop watching (capture finished / component destroyed). Idempotent. */
  stop(): void {
    this.running = false;
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
    this.worker?.postMessage({ type: 'stop' });
  }

  private async buildRequest(phase: string, ctx: Record<string, unknown>) {
    const token = (await this.sbs.supabase.auth.getSession()).data.session?.access_token ?? null;
    const ua = new UAParser().getResult();
    const build = buildDescriptor();
    return {
      url: environment.lambdaUrl + '/event_push',
      token,
      body: {
        context: {
          path: this.router.url,
          outcome: 'capture_stall',
          phase,
          // Which build produced this stall — the commit + build time (stamped at
          // build) and the runtime bundle hash (catches a stale/cached page).
          build: build.commit,
          builtAt: build.time,
          bundle: build.bundle,
          ...this.base,
          ...ctx,
        },
        type: CaptureWatchdogService.EVENT_TYPE,
        user_agent: ua,
      },
    };
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (this.workerBroken || typeof Worker === 'undefined') return null;
    try {
      const worker = this.makeWorker();
      worker.onmessage = (ev: MessageEvent) => {
        if (ev.data?.type === 'fired') {
          console.warn('[CaptureWatchdog] main-thread stall detected + logged to DB:', ev.data);
        }
      };
      worker.onerror = (ev: ErrorEvent) => {
        console.warn('[CaptureWatchdog] watchdog worker error:', ev.message);
      };
      this.worker = worker;
      return worker;
    } catch (e) {
      console.warn('[CaptureWatchdog] worker unavailable:', e);
      this.workerBroken = true;
      return null;
    }
  }

  /** Seam for tests: construct the watchdog worker. The `new Worker(new URL(...))`
   * literal must stay here so the Angular/webpack builder bundles the worker chunk. */
  protected makeWorker(): Worker {
    return new Worker(new URL('./capture-watchdog.worker', import.meta.url));
  }
}
